import api from "./client";


// ==================== FUNÇÕES DE CONFERÊNCIA DE FULL ====================

/**
 * Interface para Conferência de Transferência (Full)
 */
export interface ConferenciaFull {
	id: number;
	envio: {
		envio_id: number;
		nome: string;
		ean: string;
		status: string;
		full?: {
			full_id: number;
			full: string;
			num_envio: string;
		};
	};
	usuario: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	tarefa?: {
		tarefa_id: number;
		acao?: string;
		status?: string;
	} | null;
	status: "pendente" | "em_andamento" | "concluida" | "cancelada";
	data_inicio: string | null;
	data_conclusao: string | null;
	itens?: Array<{
		id: number;
		produto: {
			produto_id: number;
			sku: string;
			descricao: string;
			ean: string;
		};
		quantidade_esperada: number;
		quantidade_conferida: number;
		conferido: boolean;
	}>;
}


/**
 * Busca conferências de Full pendentes
 */
export async function buscarConferenciasFullPendentes(): Promise<
	ConferenciaFull[]
> {
	try {
		const response = await api.get(
			"/conferencia-transferencia/pendentes/listar",
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar conferências de Full pendentes:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
}


/**
 * Busca todas as conferências de Full (incluindo concluídas)
 */
export async function buscarTodasConferenciasFull(): Promise<
	ConferenciaFull[]
> {
	try {
		const response = await api.get("/conferencia-transferencia/listar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar todas as conferências de Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
}


export interface BuscarConferenciasFullFiltroParams {
	search?: string;
	status?: string;
	page?: number;
	limit?: number;
}


export interface BuscarConferenciasFullComFiltroResponse {
	results: ConferenciaFull[];
	page: number;
	limit: number;
	total: number;
	totalsByStatus: Record<string, number>;
	totalAll: number;
}


/**
 * Busca todas as conferências de Full com filtros e paginação
 */
export async function buscarTodasConferenciasFullComFiltro(
	params: BuscarConferenciasFullFiltroParams = {},
): Promise<BuscarConferenciasFullComFiltroResponse> {
	try {
		const searchParams = new URLSearchParams();
		if (params.search != null && params.search !== "") {
			searchParams.set("search", params.search);
		}
		if (params.status != null && params.status !== "") {
			searchParams.set("status", params.status);
		}
		if (params.page != null) {
			searchParams.set("page", String(params.page));
		}
		if (params.limit != null) {
			searchParams.set("limit", String(params.limit));
		}
		const query = searchParams.toString();
		const url = query
			? `/conferencia-transferencia/listar?${query}`
			: "/conferencia-transferencia/listar";
		const response = await api.get(url);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar conferências de Full com filtro:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
}


/**
 * Atribui usuário a uma conferência de Full
 */
export async function atribuirUsuarioConferenciaFull(
	conferenciaId: number,
	usuarioId: number,
): Promise<ConferenciaFull> {
	try {
		const response = await api.patch(
			`/conferencia-transferencia/${conferenciaId}/atribuir-usuario`,
			{
				usuario_id: usuarioId,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atribuir usuário à conferência:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
}


/**
 * Busca detalhes de uma conferência de Full
 */
export async function buscarDetalhesConferenciaFull(
	conferenciaId: number,
): Promise<ConferenciaFull> {
	try {
		const response = await api.get(
			`/conferencia-transferencia/${conferenciaId}`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar detalhes da conferência:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
}


/**
 * Criar tarefa para o Full
 * Nota: produtos_estoque é opcional - se não fornecido, o backend busca todos os produtos estoque vinculados
 */
export interface ProdutoEstoqueSelecionadoDto {
	produto_estoque_id: number;
	quantidade: number;
}


export interface CreateTarefaFullDto {
	full_id: number;
	mesa_separacao_id?: number;
	armazem_id?: number;
	produtos_estoque?: ProdutoEstoqueSelecionadoDto[];
	produto_full_ids?: number[];
}


export const criarTarefaFull = async (
	fullId: number,
	data: Omit<CreateTarefaFullDto, "full_id">,
): Promise<any> => {
	try {
		const response = await api.post(`/tarefa/${fullId}/criar-tarefa`, data);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar tarefa para o Full:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao criar tarefa para o Full.");
	}
};


/**
 * Busca todos os produtos estoque vinculados a um full
 * Retorna array de produtos estoque com informações de produto_full e localização
 */
export const buscarProdutosEstoqueVinculadosFull = async (
	fullId: number,
): Promise<
	Array<{
		produto_estoque_id: number;
		produto_full_id: number | null;
		quantidade_reservada: number;
		localizacao: {
			localizacao_id: number | null;
			armazem_id: number | null;
			nome: string | null;
			ean: string | null;
		};
	}>
> => {
	try {
		const response = await api.get(
			`/full/${fullId}/produtos-estoque-vinculados`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar produtos estoque vinculados ao Full:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar produtos estoque vinculados ao Full.");
	}
};


/**
 * Verificar se todas as tarefas de um Full estão completas
 */
export const verificarTarefasCompletasFull = async (
	fullId: number,
): Promise<{
	todas_completas: boolean;
	total_tarefas: number;
	tarefas_completas: number;
}> => {
	try {
		const response = await api.get(
			`/full/${fullId}/verificar-tarefas-completas`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao verificar tarefas completas do Full:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao verificar tarefas completas do Full.");
	}
};


/**
 * Verificar se as conferências de transferência de um Full estão completas
 */
export const verificarTransferenciasCompletasFull = async (
	fullId: number,
): Promise<{
	todas_completas: boolean;
	precisa_transferencia: boolean;
	envio_status: string | null;
	total_conferencias: number;
	concluidas: number;
	pendentes: number;
	em_andamento: number;
	canceladas: number;
}> => {
	try {
		const response = await api.get(
			`/full/${fullId}/verificar-transferencias-completas`,
		);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao verificar conferências de transferência do Full:",
			error,
		);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao verificar transferências do Full.");
	}
};


/** Relatório de Coleta Full: localizações tipo caixa com tarefas separação full concluídas no dia, qtd > 0 e sem reservado */
export interface RelatorioColetaFullResponse {
	data: string;
	por_armazem: Array<{
		armazem_id: number;
		armazem_nome: string;
		localizacoes: Array<{
			localizacao_id: number;
			nome: string;
			ean: string;
			quantidade_total: number;
			quantidade_reservada: number;
		}>;
	}>;
}


export const relatorioColetaFull = async (params?: {
	data?: string;
	armazem_id?: number;
}): Promise<RelatorioColetaFullResponse> => {
	const query: Record<string, string | number> = {};
	if (params?.data) query.data = params.data;
	if (params?.armazem_id != null) query.armazem_id = params.armazem_id;
	const response = await api.get<RelatorioColetaFullResponse>(
		"/full/relatorio-coleta",
		{ params: query },
	);
	return response.data;
};


export async function baixarRelatorioDesempenhoPdf(params: {
	tipo: "pedidos" | "full" | "reposicao";
	data: string; // YYYY-MM-DD
}): Promise<Blob> {
	const response = await api.get(`/relatorio/desempenho-pdf`, {
		params,
		responseType: "blob",
	});
	return response.data as Blob;
}


export type TipoDesempenho = "pedidos" | "full" | "reposicao";

/**
 * `data` = um dia; `dataInicio` + `dataFim` = período (até 31 dias).
 * `armazemId` filtra um dos armazéns aceitos pelo tipo.
 */
export type ParamsDesempenho = {
	tipo: TipoDesempenho;
	armazemId?: number;
} & (
	| { data: string } // YYYY-MM-DD
	| { dataInicio: string; dataFim: string }
);

export async function baixarRelatorioDesempenhoExcel(
	params: ParamsDesempenho,
): Promise<Blob> {
	const response = await api.get(`/relatorio/desempenho-excel`, {
		params,
		responseType: "blob",
	});
	return response.data as Blob;
}

/** Uma linha da tabela por operador da tela de desempenho (#1479). */
export interface PainelDesempenhoOperador {
	operadorId: number | null;
	/** Rótulo exibido: "Fulano Silva (ID: 12)". */
	operador: string;
	unidades: number;
	tarefas: number;
	naoEncontrado: number;
	tempoMedioTarefaMin: number;
	/** Quantas durações entraram no tempo médio e na mediana. */
	tarefasMedidas: number;
	medianaTarefaMin: number;
	/** Intervalos de mais de 15 min entre bips da mesma tarefa. */
	pausas: number;
	linhas: number;
	linhasPorHora: number;
	/**
	 * Turno líquido (descontando 1h de almoço quando passa de 6h), somado só
	 * nos dias com turno válido.
	 */
	turnoLiquidoMin: number;
	/**
	 * Janela do turno cadastrado, em minutos desde a meia-noite. `null` sem
	 * horário cadastrado — ou, no período, quando a janela muda entre os dias.
	 */
	turnoInicioMin: number | null;
	turnoFimMin: number | null;
	/**
	 * `false` quando não há turno cadastrado OU quando termina às 23:59 (que é
	 * "não sei quando termina"). Nos dois casos Un/h e aderência não têm
	 * denominador confiável e a tela mostra "—" em vez de número.
	 */
	turnoValido: boolean;
	aderenciaTurnoPct: number;
	unidadesPorHora: number;
	/** Só no Dia. */
	finalizadas?: number;
	/** Só no Período. */
	diasTrabalhados?: number;
	unidadesPorDia?: number;
	/** Só no Período: dias trabalhados com turno válido. */
	diasComTurno?: number;
}

/** Uma linha do mapa de calor operador × hora. */
export interface PainelDesempenhoOperadorHora {
	operadorId: number | null;
	operador: string;
	/** Unidades bipadas em cada hora, alinhado ao vetor `horas`. */
	unidades: number[];
	mediaPorHora: number;
}

/** Uma linha do mapa de calor operador × dia. */
export interface PainelDesempenhoOperadorDia {
	operadorId: number | null;
	operador: string;
	/** Unidades bipadas em cada dia, alinhado ao vetor `dias`. */
	unidades: number[];
	total: number;
}

export interface PainelDesempenhoResumo {
	unidades: number;
	operadores: number;
	tarefas: number;
	unidadesPorHoraMedia: number;
	tempoMedioTarefaMin: number;
	tarefasMedidas: number;
	medianaTarefaMin: number;
	pausas: number;
	unidadesPorTarefa: number;
	linhas: number;
	/** Só no Dia. */
	tarefasFinalizadas?: number;
	/** Só no Período. */
	diasComMovimento?: number;
	unidadesPorDia?: number;
}

interface PainelDesempenhoBase {
	tipo: TipoDesempenho;
	armazemId: number | null;
	/** Opções do filtro de armazém, pelo nome do cadastro. */
	armazensDisponiveis: Array<{ id: number; nome: string }>;
	/** Linhas e Linhas/h só aparecem nos tipos em que o backend liga (Full). */
	mostrarLinhas: boolean;
	resumo: PainelDesempenhoResumo;
	operadores: PainelDesempenhoOperador[];
}

/**
 * Payload da tela no modo Dia: espelha as abas Resumo, Por operador,
 * Por hora e Operador x hora da planilha (#1479).
 */
export interface PainelDesempenhoDia extends PainelDesempenhoBase {
	modo: "dia";
	data: string;
	/** Horas com movimento no dia (as pontas zeradas são recortadas). */
	horas: number[];
	/** Unidades bipadas em cada hora, alinhado ao vetor `horas`. */
	porHora: number[];
	operadorHora: PainelDesempenhoOperadorHora[];
}

/** Payload da tela no modo Período: cada dia com a regra do Dia, juntado. */
export interface PainelDesempenhoPeriodo extends PainelDesempenhoBase {
	modo: "periodo";
	dataInicio: string;
	dataFim: string;
	/** Todos os dias do período, inclusive os sem movimento. */
	dias: string[];
	porDia: number[];
	operadorDia: PainelDesempenhoOperadorDia[];
}

export type PainelDesempenho = PainelDesempenhoDia | PainelDesempenhoPeriodo;

/**
 * Números do desempenho para exibir na tela (#1479) — os mesmos que
 * alimentam a planilha, sem precisar baixá-la.
 */
export async function buscarPainelDesempenho(
	params: ParamsDesempenho,
): Promise<PainelDesempenho> {
	const response = await api.get(`/relatorio/desempenho`, { params });
	return response.data as PainelDesempenho;
}


// ==================== FUNÇÕES DE DIVERGÊNCIAS CONFERÊNCIA FULL ====================

/**
 * Interface para Divergência da Conferência Full
 */
export interface DivergenciaConferenciaFull {
	id: number;
	produto: {
		produto_id: number;
		sku: string;
		descricao: string;
		ean: string;
	};
	quantidade_esperada: number;
	quantidade_conferida: number;
	diferenca: number;
	tipo: "sobra" | "falta";
	tratada: boolean;
	criado_em: string;
}


/**
 * Interface para Conferência Full com divergências
 */
export interface ConferenciaFullComDivergencias {
	id: number;
	envio: {
		envio_id: number;
		nome: string;
		ean: string;
		status: string;
		full?: {
			full_id: number;
			full: string;
			num_envio: string;
			num_pedido?: string;
			faturado?: boolean;
		};
	};
	armazem?: {
		armazem_id: number;
		nome: string;
	};
	status:
		| "pendente"
		| "em_andamento"
		| "concluida"
		| "pre_conferida"
		| "concluida_validada"
		| "concluida_com_divergencia"
		| "cancelada";
	divergencia_tratada: boolean;
	data_inicio: string | null;
	data_conclusao: string | null;
	itens?: Array<{
		id: number;
		produto: {
			produto_id: number;
			sku: string;
			descricao: string;
			ean: string;
		};
		quantidade_esperada: number;
		quantidade_conferida: number;
		conferido: boolean;
	}>;
	divergencias?: DivergenciaConferenciaFull[];
}


/**
 * Interface para item da lista de Conferência Full (listar / pendentes)
 */
export interface ConferenciaFullListItem {
	id: number;
	envio: {
		envio_id: number;
		nome: string;
		ean: string;
		status?: string;
		full?: {
			full_id: number;
			full: string;
			num_envio: string;
			num_pedido?: string;
			faturado?: boolean;
		};
	};
	armazem?: {
		armazem_id: number;
		nome: string;
	};
	/**
	 * Armazéns de origem do envio (exclui armazém master), usado no fluxo 1 conferência por envio.
	 */
	armazens_envio?: Array<{ armazem_id: number; nome: string }>;
	usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
		perfil?: {
			perfil_id: number;
			nome?: string;
			funcao?: string[];
		};
	} | null;
	status:
		| "pendente"
		| "em_andamento"
		| "concluida"
		| "pre_conferida"
		| "concluida_validada"
		| "concluida_com_divergencia"
		| "cancelada";
	data_inicio: string | null;
	data_conclusao: string | null;
}


/**
 * Lista conferências Full pendentes (pendente, em_andamento, pre_conferida)
 */
export const listarConferenciasFullPendentes = async (): Promise<
	ConferenciaFullListItem[]
> => {
	try {
		const response = await api.get("/conferencia-full/pendentes/listar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar conferências Full pendentes:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Lista todas as conferências Full
 */
export const listarConferenciasFullTodas = async (): Promise<
	ConferenciaFullListItem[]
> => {
	try {
		const response = await api.get("/conferencia-full/listar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar conferências Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Atribui usuário a uma conferência Full (nova lista / conferencia-full)
 */
export const atribuirUsuarioConferenciaFullPorId = async (
	conferenciaId: number,
	usuarioId: number,
): Promise<ConferenciaFullComDivergencias> => {
	try {
		const response = await api.patch(
			`/conferencia-full/${conferenciaId}/atribuir-usuario`,
			{ usuario_id: usuarioId },
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atribuir usuário à conferência Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Remove atribuição de usuário de uma conferência Full
 */
export const removerAtribuicaoConferenciaFull = async (
	conferenciaId: number,
): Promise<ConferenciaFullComDivergencias> => {
	try {
		const response = await api.post(
			`/conferencia-full/${conferenciaId}/remover-atribuicao`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao remover atribuição da conferência Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Busca detalhes de uma conferência Full (incluindo divergências)
 */
export const buscarDetalhesConferenciaFullComDivergencias = async (
	conferenciaId: number,
): Promise<ConferenciaFullComDivergencias> => {
	try {
		const response = await api.get(`/conferencia-full/${conferenciaId}`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar detalhes da conferência Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Busca detalhes "lite" de uma conferência Full (sem itens) para abrir telas/modais rápido
 */
export const buscarDetalhesConferenciaFullComDivergenciasLite = async (
	conferenciaId: number,
): Promise<ConferenciaFullComDivergencias> => {
	try {
		const response = await api.get(`/conferencia-full/${conferenciaId}`, {
			params: { lite: 1 },
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar detalhes lite da conferência Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


export interface ItemConferenciaFull {
	id: number;
	quantidade_esperada: number;
	quantidade_conferida: number;
	conferido: boolean;
	produto: {
		produto_id: number;
		sku: string;
		descricao: string;
		ean: string;
	};
}


export const listarItensConferenciaFull = async (
	conferenciaId: number,
	params?: { take?: number; skip?: number },
): Promise<{
	items: ItemConferenciaFull[];
	total: number;
	take: number;
	skip: number;
}> => {
	try {
		const response = await api.get(`/conferencia-full/${conferenciaId}/itens`, {
			params,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar itens da conferência Full:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


export interface TarefaDoProdutoConferenciaFull {
	tarefa_id: number;
	status: string;
	usuario: { usuario_id: number; nome: string } | null;
	quantidade_total: number;
	quantidade_bipada: number;
	concluida: boolean;
	armazem: string | null;
	data_inicio: string | null;
	data_termino: string | null;
}


export const listarTarefasDoProdutoConferenciaFull = async (
	conferenciaId: number,
	produtoId: number,
): Promise<TarefaDoProdutoConferenciaFull[]> => {
	try {
		const response = await api.get(
			`/conferencia-full/${conferenciaId}/itens/${produtoId}/tarefas`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar tarefas do produto:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Marca uma divergência da conferência Full como tratada
 */
export const marcarDivergenciaTratada = async (
	divergenciaId: number,
): Promise<{
	divergencia_id: number;
	tratada: boolean;
	conferencia_divergencia_tratada: boolean;
}> => {
	try {
		const response = await api.post(
			`/conferencia-full/divergencia/${divergenciaId}/marcar-tratada`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao marcar divergência como tratada:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Busca ou cria a localização "Sobras Full" no armazém especificado
 */
export const getOuCriarLocalizacaoSobrasFull = async (
	armazem: string,
): Promise<{
	localizacao_id: number;
	nome: string;
	ean: string;
	armazem: { armazem_id: number; nome: string };
} | null> => {
	try {
		const response = await api.get(
			`/localizacao/sobras-full?armazem=${encodeURIComponent(armazem)}`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar/criar localização Sobras Full:", error);
		return null;
	}
};


// ==================== FUNÇÕES DE BIPAGEM CONFERÊNCIA FULL ====================

/**
 * Interface para uma bipagem individual
 */
export interface BipagemConferenciaFull {
	id: number;
	conferencia_id: number;
	item_id: number;
	usuario_id: number;
	produto_id: number;
	quantidade: number;
	data_hora: string;
	produto?: {
		produto_id: number;
		sku: string;
		descricao: string;
		ean: string;
	};
	usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	};
}


/**
 * Interface para o progresso da conferência
 */
export interface ProgressoConferenciaFull {
	total_esperado: number;
	total_conferido: number;
	percentual: number;
	itens_total: number;
	itens_conferidos: number;
}


/**
 * Scan inválido persistido (agrupado por código): produto que existe no WMS mas
 * não é do full (`nao_pertence`) ou código não cadastrado (`nao_existe`).
 * `excedente` (#1893) é produto do full bipado além do esperado: a leitura foi
 * recusada e fica só como auditoria — não é "Fora do Full".
 */
export interface ScanInvalidoConferenciaFull {
	id: number;
	codigo: string;
	tipo: "nao_existe" | "nao_pertence" | "localizacao" | "excedente";
	produto_id: number | null;
	descricao: string | null;
	sku: string | null;
	quantidade: number;
}


/**
 * Interface para resultado de bipagem
 */
export interface ResultadoBipagem {
	// 'ok' = bipou um item do full. Os demais são "soft errors" (modo soft):
	// o backend responde 200 com o resultado em vez de lançar 400/404.
	tipo?: "ok" | "nao_existe" | "nao_pertence" | "localizacao" | "excedente";
	message?: string;
	codigo?: string;
	item?: {
		id: number;
		quantidade_esperada: number;
		quantidade_conferida: number;
		conferido: boolean;
	};
	produto?: {
		produto_id: number;
		sku: string;
		descricao: string;
		ean: string;
	};
	quantidade_bipada?: number;
	quantidade_conferida?: number;
	quantidade_esperada?: number;
	conferido?: boolean;
	// Otimização: o backend já devolve o progresso atualizado e a bipagem
	// criada, evitando GETs extras de progresso/bipagens a cada bip.
	progresso?: ProgressoConferenciaFull;
	bipagem?: BipagemConferenciaFull;
	// Presente quando tipo é um soft error (nao_existe/nao_pertence/excedente).
	scanInvalido?: ScanInvalidoConferenciaFull;
}

/** Erro de bipagem com dados estruturados (vem do corpo do erro do backend). */
export interface ErroBipagem extends Error {
	tipo?: "nao_existe" | "nao_pertence" | "localizacao" | "excedente";
	codigo?: string;
	produto?: ResultadoBipagem["produto"];
	scanInvalido?: ScanInvalidoConferenciaFull;
}


/**
 * Bipa um produto na conferência Full
 */
export const biparProdutoConferenciaFull = async (
	conferenciaId: number,
	codigo: string,
	usuarioId: number,
	quantidade = 1,
): Promise<ResultadoBipagem> => {
	try {
		const response = await api.post(
			`/conferencia-full/${conferenciaId}/bipar-produto`,
			{
				codigo,
				quantidade,
				usuario_id: usuarioId,
				// Web: erros de negócio voltam 200 (sem 400/404 no console).
				soft: true,
			},
		);
		return response.data;
	} catch (error: any) {
		// Erros de negócio (nao_existe / nao_pertence) vêm com corpo estruturado:
		// o backend grava o scan inválido e devolve tipo/scanInvalido. Repassamos
		// num Error enriquecido pra a tela montar a lista vermelha.
		const data = error.response?.data;
		const mensagem = data?.message || error.message;
		const err = new Error(mensagem) as ErroBipagem;
		if (data?.tipo) err.tipo = data.tipo;
		if (data?.codigo) err.codigo = data.codigo;
		if (data?.produto) err.produto = data.produto;
		if (data?.scanInvalido) err.scanInvalido = data.scanInvalido;
		throw err;
	}
};


/**
 * Corrige o excedente de um produto (ação de supervisor — exige `editar`):
 * baixa a quantidade conferida para a esperada. Retorna o item ajustado e o
 * progresso atualizado.
 */
export const corrigirExcessoConferenciaFull = async (
	conferenciaId: number,
	produtoId: number,
): Promise<{
	item: {
		id: number;
		produto_id: number;
		quantidade_conferida: number;
		quantidade_esperada: number;
		conferido: boolean;
	};
	progresso: ProgressoConferenciaFull;
}> => {
	try {
		const response = await api.post(
			`/conferencia-full/${conferenciaId}/corrigir-excesso`,
			{ produto_id: produtoId },
		);
		return response.data;
	} catch (error: any) {
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * "Zerar EANs fora do full" (ação de supervisor — exige `editar`): apaga todos
 * os itens "fora do full" da conferência. Retorna quantos foram removidos.
 */
export const zerarForaDoFullConferenciaFull = async (
	conferenciaId: number,
): Promise<{ removidos: number }> => {
	try {
		const response = await api.post(
			`/conferencia-full/${conferenciaId}/zerar-fora-do-full`,
		);
		return response.data;
	} catch (error: any) {
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Lista os scans inválidos persistidos de uma conferência (não pertence / não
 * cadastrado), agrupados por código.
 */
export const listarScansInvalidosConferenciaFull = async (
	conferenciaId: number,
): Promise<ScanInvalidoConferenciaFull[]> => {
	try {
		const response = await api.get(
			`/conferencia-full/${conferenciaId}/scans-invalidos`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar scans inválidos:", error);
		return [];
	}
};


/**
 * Lista as bipagens de uma conferência Full.
 * Aceita paginação (take/skip); sem ela o backend retorna todas — evite em
 * conferências grandes (a lista cresce a cada bip e fica O(n²) no fluxo de
 * bipagem). A tela de execução pede só as mais recentes (take pequeno).
 */
export const listarBipagensConferenciaFull = async (
	conferenciaId: number,
	opts?: { take?: number; skip?: number },
): Promise<BipagemConferenciaFull[]> => {
	try {
		const response = await api.get(
			`/conferencia-full/${conferenciaId}/bipagens`,
			{
				params: {
					...(opts?.take != null ? { take: opts.take } : {}),
					...(opts?.skip != null ? { skip: opts.skip } : {}),
				},
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar bipagens:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Obtém o progresso da conferência Full
 */
export const obterProgressoConferenciaFull = async (
	conferenciaId: number,
): Promise<ProgressoConferenciaFull> => {
	try {
		const response = await api.get(
			`/conferencia-full/${conferenciaId}/progresso`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao obter progresso:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * P9: Sincroniza quantidade esperada com bipagem real do Full (endpoint POST dedicado).
 * Chamar antes de exibir itens/progresso quando necessário.
 */
export const sincronizarEsperadoConferenciaFull = async (
	conferenciaId: number,
): Promise<void> => {
	try {
		await api.post(`/conferencia-full/${conferenciaId}/sincronizar-esperado`);
	} catch (error: any) {
		console.warn("Erro ao sincronizar esperado:", error?.message);
	}
};


/**
 * Finaliza uma conferência Full
 */
export const finalizarConferenciaFull = async (
	conferenciaId: number,
	usuarioId: number,
): Promise<ConferenciaFullComDivergencias> => {
	try {
		const response = await api.post(
			`/conferencia-full/${conferenciaId}/finalizar`,
			{
				usuario_id: usuarioId,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao finalizar conferência:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Reiniciar uma conferência zerando todas as quantidades bipadas
 */
export const reiniciarConferenciaFull = async (
	conferenciaId: number,
): Promise<ConferenciaFullComDivergencias> => {
	try {
		const response = await api.post(
			`/conferencia-full/${conferenciaId}/reiniciar`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao reiniciar conferência:", error);
		const mensagem = error.response?.data?.message || error.message;
		throw new Error(mensagem);
	}
};


/**
 * Verificar se as conferências Full do envio permitem faturar (todas validadas ou divergência tratada)
 */
export const verificarConferenciasFull = async (
	fullId: number,
): Promise<{
	pode_faturar: boolean;
	motivo?: string;
	conferencias: Array<{
		id: number;
		status: string;
		divergencia_tratada: boolean;
		armazem_id?: number;
	}>;
}> => {
	try {
		const response = await api.get(
			`/full/${fullId}/verificar-conferencias-full`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao verificar conferências Full:", error);
		const mensagem =
			error?.response?.data?.message ||
			error?.message ||
			"Falha ao verificar conferências Full";
		return { pode_faturar: false, motivo: mensagem, conferencias: [] };
	}
};


/**
 * Buscar relatório geral do Full
 */
export const buscarRelatorioGeralFull = async (
	fullId: number,
): Promise<{
	informacoes_full: {
		full_id: number;
		full: string;
		num_envio: string;
		num_pedido: string;
		faturado: boolean;
		estoque_confirmado: boolean;
		cancelado: boolean;
		data_hora_inclusao: string;
		data_hora_envio: string;
		envio_nome?: string;
		envio_ean?: string;
	};
	informacoes_tarefas: {
		total_tarefas: number;
		tarefas_pendentes: number;
		tarefas_em_andamento: number;
		tarefas_concluidas: number;
		tarefas_canceladas: number;
		tarefas_ids: number[];
	};
	produtos_nao_encontrados: Array<{
		produto_id: number;
		sku: string;
		descricao: string;
		ean: string | null;
		quantidade_nao_encontrada: number;
		localizacao_esperada: {
			localizacao_id: number;
			nome: string;
			codigo: string;
			armazem: string;
		} | null;
		data_ocorrencia: string;
	}>;
	produtos_bipados: Array<{
		produto_id: number;
		sku: string;
		descricao: string;
		ean: string | null;
		quantidade_bipada: number;
		quantidade_total: number;
		progresso: number;
		localizacao_bipada: {
			localizacao_id: number;
			nome: string;
			codigo: string;
			armazem: string;
		} | null;
	}>;
	progressao_geral: {
		total_produtos_full: number;
		total_unidades_esperadas: number;
		total_unidades_bipadas: number;
		total_unidades_nao_encontradas: number;
		progresso_percentual: number;
		produtos_completos: number;
		produtos_pendentes: number;
	};
	estatisticas_armazens: Array<{
		armazem_id: number;
		armazem_nome: string;
		produtos_unicos: number;
		unidades_esperadas: number;
		unidades_bipadas: number;
		produtos_nao_encontrados: number;
		unidades_nao_encontradas: number;
	}>;
}> => {
	try {
		const response = await api.get(`/full/${fullId}/relatorio-geral`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar relatório geral do Full:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar relatório geral do Full.");
	}
};



